import React from "react";
import { makeStyles, withStyles, createStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { updateJobStatus } from "../redux_store/jobState";
import { useSelector } from "react-redux";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import DoneIcon from "@material-ui/icons/Done";
import Select from "@material-ui/core/Select";
import { Palette } from "../AppConfig";
import { addStatusTrackingEntry } from "../helper/Helper";

let Lysaght = false;
let Fielders = false;

const useStyles = makeStyles((theme) => ({
    LysaghtRoot: {
        backgroundColor: "#E9EEF6",
        borderRadius: "10px !important",
        color: "#3b64ab",
        height: "20px",
        "& > svg": {
            visibility: "hidden",
            width: "0px",
            height: "0px",
        },
        "& > div > div": {
            visibility: "hidden",
            width: "0px !important",
            height: "0px !important",
            margin: "0px !important",
        },
    },
    FildersRoot: {
        border: "3px solid #5E3CBA",
        backgroundColor: "#eae5f6",
        borderColor: "#5E3CBA",
        color: "#5E3CBA",
    },
    root: {
        borderRadius: "6px",
        textAlign: "center",
        textTransform: "uppercase",
        font: "9pt " + theme.typography.fontFamily,
        padding: "6px 4px 1px 4px",
        margin: "4px 0px",
        width: "fit-content",
        minWidth: "80px",
    },
    selectRoot: {
        "&:hover": { backgroundColor: "#3b64ab" },
    },
    menuItem: {
        font: "10pt " + theme.typography.fontFamily,
    },
    loading: {
        width: "10px !important",
        height: "10px !important",
        display: "block",
        position: "absolute",
        top: "11px",
        right: "-15px",
    },
}));

const allActiveStatuses = [
    "New",
    "Design",
    "Quoted",
    "Won",
    "Lost",
    "Deposit Received",
    "With Engineer",
    "With Council",
    "Invoiced",
    "Ordered",
    "Delivered",
    "Construction",
    "Complete",
];

const obsoleteActiveStatuses = new Set(["New", "Deposit Received", "Invoiced", "Delivered", "Construction", "Complete"]);

const inactiveStatuses = ["deleted", "archived", "test"];

// Manual transition rules
const getManuallySelectable = (currentStatus) => {
    const cur = (currentStatus || "").toLowerCase();

    const selectable = new Set(["Lost"]);

    // Hide additional rules until further notice
    const isActive = false;
    if (cur === "design" && isActive) {
        selectable.add("With Engineer");
        selectable.add("With Council");
    }

    if ((cur === "with engineer" || cur === "with council") && isActive) {
        selectable.add("Design");
    }

    return selectable;
};

const buildVisibleStatuses = (currentStatus) => {
    if (!currentStatus) return ["Lost"];

    const manual = Array.from(getManuallySelectable(currentStatus));
    const result = [currentStatus, ...manual];
    const seen = new Set();

    return result.filter((s) => {
        const key = (s || "").toLowerCase();

        if (!key) return false;
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};

export const CustomMenuItem = withStyles((theme) =>
    Lysaght
        ? createStyles({
              root: {
                  lineHeight: 0.1,
                  paddingTop: "3px",
                  paddingBottom: "3px",
                  "&$selected": {
                      backgroundColor: Palette.Lysaght.DarkBlue,
                      color: "white",
                  },
              },
              selected: {},
          })
        : createStyles({})
)(MenuItem);

export const normaliseStatus = (status) => {
    if (!status || inactiveStatuses.includes(status.toLowerCase())) return null;

    const normalisedStatus = status.toLowerCase();
    return allActiveStatuses.find((s) => s.toLowerCase() === normalisedStatus) || status;
};

export default function BSL_StatusChip(props) {
    Lysaght = window.brand === "Lysaght" || props?.displayOnly;
    Fielders = !Lysaght;

    const classes = useStyles();
    const { job_id, history, displayOnly, businessCode } = props;

    const [loading, setLoading] = React.useState(false);
    const [localStatusState, setLocalStatusState] = React.useState(null);
    const [localHistoryState, setLocalHistoryState] = React.useState(history || {});

    const user = useSelector((state) => state.app.user);
    const userBusinessCode = user?.BusinessDetails?.Code;
    const status = normaliseStatus(props.status);

    if (!status) return null;

    const currentValue = localStatusState || status;
    const visibleMenuStatuses = displayOnly ? [currentValue] : buildVisibleStatuses(currentValue);

    const handleSelected = () =>
        Lysaght && (
            <div style={{ marginRight: "15px", marginTop: "5px", color: "white" }}>
                <DoneIcon />
            </div>
        );

    const formatLabel = (value) => {
        return obsoleteActiveStatuses.has(value) ? `${value} *` : value;
    };

    const MenuItems = (list) =>
        list.map((e) => (
            <CustomMenuItem key={e} value={e} className={classes.menuItem}>
                {handleSelected()}
                {formatLabel(e)}
            </CustomMenuItem>
        ));

    return (
        <FormControl
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <Select
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                value={currentValue}
                disabled={displayOnly}
                onChange={async (e) => {
                    const newStatus = e.target.value;

                    if (!getManuallySelectable(currentValue).has(newStatus)) return;

                    try {
                        if (!userBusinessCode) return;

                        setLoading(true);

                        const currentStatus = props?.newJobState?.Status || localStatusState || status;

                        if (newStatus === currentStatus) return;

                        setLocalStatusState(newStatus);

                        let existingTracking;
                        let updatedTracking;

                        if (props.newJobState) {
                            existingTracking = props.newJobState.StatusTracking || {};
                            updatedTracking = addStatusTrackingEntry(newStatus, existingTracking);

                            const newJobState = {
                                ...props.newJobState,
                                Status: newStatus,
                                StatusTracking: updatedTracking,
                            };

                            props.setNewJobState(newJobState);
                        } else {
                            existingTracking = localHistoryState || {};
                            updatedTracking = addStatusTrackingEntry(newStatus, existingTracking);

                            setLocalHistoryState(updatedTracking);

                            await updateJobStatus(job_id, businessCode, newStatus, updatedTracking);
                        }
                    } finally {
                        setLoading(false);
                    }
                }}
                disableUnderline
                className={`${classes.root} ${Fielders && classes.FildersRoot} ${Lysaght && classes.LysaghtRoot}`}
                SelectDisplayProps={{
                    style: Fielders ? { padding: "0px 24px 0px 0px" } : { padding: "0px" },
                }}
                inputProps={Fielders ? { MenuProps: { disableScrollLock: true } } : undefined}
                MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                }}
            >
                {MenuItems(visibleMenuStatuses)}
            </Select>

            {loading && <CircularProgress className={classes.loading} />}
        </FormControl>
    );
}
